import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search, Filter, ChevronRight, ChevronDown, CheckCircle,
    AlertTriangle, Clock, Calendar, Database, Shield,
    Zap, Copy, Layout, Layers, AlertCircle, Download,
    Users, User, DollarSign, Star, Info, Brain, Save, FileText, Plus,
    Trash, Edit, BarChart3, Eye, UserCheck, Lock, Unlock, Send, Archive
} from 'lucide-react';

import ReactDOM from 'react-dom';
import { useAuth } from '../../../context/AuthContext';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx';
import { saveAs } from '../../../utils/download';
import useImplementationChecklistStore from '../../../stores/useImplementationChecklistStore';
import useSuiviMeasures from './useSuiviMeasures';

const GlobalUnifiedChecklist = ({ analysisId, selectedResponsible }) => {
    const { t, i18n } = useTranslation();
    const { isAdmin } = useAuth();
    const currentLang = i18n.language;

    const checklistData = useImplementationChecklistStore((s) => s.checklistData);
    const checklistQuestions = useImplementationChecklistStore((s) => s.checklistQuestions);
    const checklistHistory = useImplementationChecklistStore((s) => s.checklistHistory);
    const checklistGenerated = useImplementationChecklistStore((s) => s.checklistGenerated);
    const approvalStatus = useImplementationChecklistStore((s) => s.approvalStatus);
    const versionInfo = useImplementationChecklistStore((s) => s.versionInfo);
    const isLoading = useImplementationChecklistStore((s) => s.isLoading);
    const isGenerating = useImplementationChecklistStore((s) => s.isGenerating);
    const isSaving = useImplementationChecklistStore((s) => s.isSaving);
    const error = useImplementationChecklistStore((s) => s.error);
    const generationProgress = useImplementationChecklistStore((s) => s.generationProgress);

    const loadGlobalChecklist = useImplementationChecklistStore((s) => s.loadGlobalChecklist);
    const generateChecklistAction = useImplementationChecklistStore((s) => s.generateChecklist);
    const deriveQuestions = useImplementationChecklistStore((s) => s.deriveQuestions);    const setResponse = useImplementationChecklistStore((s) => s.setResponse);
    const setJustification = useImplementationChecklistStore((s) => s.setJustification);
    const saveResponses = useImplementationChecklistStore((s) => s.saveResponses);
    const submitForApproval = useImplementationChecklistStore((s) => s.submitForApproval);
    const approveAction = useImplementationChecklistStore((s) => s.approve);
    const rejectAction = useImplementationChecklistStore((s) => s.reject);
    const unlockAction = useImplementationChecklistStore((s) => s.unlock);
    const createGlobalSnapshot = useImplementationChecklistStore((s) => s.createGlobalSnapshot);
    const addQuestionAction = useImplementationChecklistStore((s) => s.addQuestion);
    const editQuestionAction = useImplementationChecklistStore((s) => s.editQuestion);
    const deleteQuestionAction = useImplementationChecklistStore((s) => s.deleteQuestion);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilters, setSelectedFilters] = useState({
        status: [], category: [], priority: [], timeline: [], source: []
    });
    const [groupBy, setGroupBy] = useState('category');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [showTimeline, setShowTimeline] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [activeTab, setActiveTab] = useState('checklist');
    const [showOnlyUnanswered, setShowOnlyUnanswered] = useState(false);

    const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
    const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [newQuestionText, setNewQuestionText] = useState('');
    const [newQuestionCategory, setNewQuestionCategory] = useState('Technique');
    const [newQuestionResponsible, setNewQuestionResponsible] = useState('');
    const [selectedMeasures, setSelectedMeasures] = useState([]);

    const [dialogConfig, setDialogConfig] = useState({
        isOpen: false, type: 'confirm', title: '', message: '',
        onConfirm: null, onCancel: null, inputValue: '',
        confirmLabel: t('common.confirm') || 'Confirmer',
        cancelLabel: t('common.cancel') || 'Annuler',
        placeholder: ''
    });
    const [currentVersionIndex, setCurrentVersionIndex] = useState(null);
    const [isOnBranch, setIsOnBranch] = useState(false);

    const adminMode = isAdmin;

    // Measures come from the Atelier 5 measures table (same builder rows as
    // SecurityMeasuresTable). Backend checklist data is kept only for
    // questions / approvals; its `measures` snapshot is a fallback until the
    // Atelier 5 store is ready.
    const { allMeasures: suiviMeasures, isReady: suiviReady } = useSuiviMeasures(analysisId);
    const sourceMeasures = useMemo(() => (
      suiviReady ? suiviMeasures : (checklistData?.measures || [])
    ), [suiviReady, suiviMeasures, checklistData]);

    useEffect(() => {
        if (analysisId) {
            loadGlobalChecklist(analysisId);
        }
    }, [analysisId, loadGlobalChecklist]);

    useEffect(() => {
        if (!checklistData) return;
        if (currentVersionIndex !== null) return;
        deriveQuestions(selectedResponsible, sourceMeasures);
    }, [checklistData, selectedResponsible, sourceMeasures, deriveQuestions, currentVersionIndex]);

    useEffect(() => {
        setCurrentVersionIndex(null);
        setIsOnBranch(false);
    }, [selectedResponsible]);

    const handleGenerateChecklist = async () => {
        await generateChecklistAction({ analysisId, language: currentLang, allMeasures: sourceMeasures });
    };

    const handleChecklistResponseChange = (questionId, response) => {
        const result = setResponse(questionId, response, selectedResponsible);
        if (result?.locked) {
            alert(result.message);
        }
    };

    const handleJustificationChange = (questionId, justification) => {
        setJustification(questionId, justification, selectedResponsible);
    };

    const handleSaveChecklistResponses = () => {
        saveResponses({ selectedResponsible, updateCurrentVersion: true, skipReload: true });
    };

    const handleSubmitChecklist = () => {
        setDialogConfig({
            isOpen: true, type: 'confirm',
            title: 'Soumettre pour approbation',
            message: 'Êtes-vous sûr de vouloir soumettre cette checklist pour approbation ? \n\nVous ne pourrez plus la modifier tant qu\'elle n\'aura pas été traitée par un administrateur.',
            confirmLabel: 'Soumettre',
            onConfirm: () => submitForApproval(selectedResponsible)
        });
    };

    const handleApproveChecklist = () => {
        setDialogConfig({
            isOpen: true, type: 'confirm',
            title: 'Approuver la checklist',
            message: 'Confirmer l\'approbation de cette checklist ? \n\nCette version sera validée et verrouillée définitivement.',
            confirmLabel: 'Approuver',
            onConfirm: () => approveAction(selectedResponsible)
        });
    };

    const handleRejectChecklist = () => {
        setDialogConfig({
            isOpen: true, type: 'prompt',
            title: 'Rejeter la checklist',
            message: 'Veuillez indiquer la raison du rejet pour aider le responsable à corriger les réponses.',
            confirmLabel: 'Rejeter',
            placeholder: 'Saisissez la raison ici...',
            onConfirm: (reason) => {
                if (!reason || !reason.trim()) {
                    alert('La raison du rejet est obligatoire.');
                    return;
                }
                rejectAction(selectedResponsible, reason);
            }
        });
    };

    const handleUnlockChecklist = () => {
        setDialogConfig({
            isOpen: true, type: 'confirm',
            title: 'Déverrouiller la version',
            message: 'Attention : Déverrouiller manuellement cette version permettra de nouvelles modifications. Cette action peut affecter l\'historique. Continuer ?',
            confirmLabel: 'Déverrouiller',
            onConfirm: () => unlockAction(selectedResponsible)
        });
    };

    const handleAddQuestion = () => {
        if (!newQuestionText.trim()) {
            setDialogConfig({ isOpen: true, type: 'alert', title: 'Champ requis', message: 'Veuillez saisir une question avant de continuer.', confirmLabel: 'D\'accord' });
            return;
        }
        addQuestionAction({
            question: newQuestionText,
            category: newQuestionCategory,
            concerned_measures: selectedMeasures.map((m) => m.name || m.title || m.measureName),
            concerned_measure_ids: selectedMeasures.map((m) => m.id),
            responsiblePerson: newQuestionResponsible || null
        });
        if (!selectedResponsible) {
            saveResponses({ updateCurrentVersion: true, skipReload: true });
        }
        setNewQuestionText(''); setNewQuestionCategory('Technique');
        setNewQuestionResponsible(''); setSelectedMeasures([]);
        setShowAddQuestionModal(false);
    };

    const handleEditQuestion = () => {
        if (!newQuestionText.trim() || !editingQuestion) {
            alert('Veuillez saisir une question');
            return;
        }
        editQuestionAction(editingQuestion.id, {
            question: newQuestionText,
            category: newQuestionCategory,
            concerned_measures: selectedMeasures.map((m) => m.name || m.title || m.measureName),
            concerned_measure_ids: selectedMeasures.map((m) => m.id),
            responsiblePerson: newQuestionResponsible || null
        });
        if (!selectedResponsible) {
            saveResponses({ updateCurrentVersion: true, skipReload: true });
        }
        setEditingQuestion(null);
        setNewQuestionText(''); setNewQuestionCategory('Technique');
        setNewQuestionResponsible(''); setSelectedMeasures([]);
        setShowEditQuestionModal(false);
    };

    const handleDeleteQuestion = (questionId) => {
        setDialogConfig({
            isOpen: true, type: 'confirm',
            title: 'Supprimer la question',
            message: 'Êtes-vous sûr de vouloir supprimer cette question ? Cette action est irréversible.',
            confirmLabel: 'Supprimer', cancelLabel: 'Annuler',
            onConfirm: () => {
                deleteQuestionAction(questionId);
                if (!selectedResponsible) {
                    saveResponses({ updateCurrentVersion: true, skipReload: true });
                }
            }
        });
    };

    const openEditModal = (question) => {
        setEditingQuestion(question);
        setNewQuestionText(question.question);
        setNewQuestionCategory(question.category || 'Technique');
        setNewQuestionResponsible(question.responsiblePerson || '');
        const questionMeasures = sourceMeasures.filter((m) => {
            const displayName = m.name || m.title || m.measureName;
            return question.concerned_measure_ids?.includes(m.id) ||
                (displayName && question.concerned_measures?.includes(displayName));
        });
        setSelectedMeasures(questionMeasures);
        setShowEditQuestionModal(true);
    };

    const toggleMeasureSelection = (measure) => {
        setSelectedMeasures((prev) => {
            const exists = prev.find((m) => m.id === measure.id);
            if (exists) return prev.filter((m) => m.id !== measure.id);
            return [...prev, measure];
        });
    };

    const loadVersion = (versionIndex) => {
        if (checklistHistory[versionIndex]) {
            const version = checklistHistory[versionIndex];
            useImplementationChecklistStore.setState({ checklistQuestions: version.questions });
            setCurrentVersionIndex(versionIndex);
            setIsOnBranch(versionIndex !== checklistHistory.length - 1);
        }
    };

    const handleCreateGlobalSnapshot = () => {
        setDialogConfig({
            isOpen: true, type: 'confirm',
            title: 'Valider la Version Globale',
            message: 'Toutes les checklists responsables sont approuvées. \n\nVoulez-vous créer une validation globale (Snapshot) et passer à la version suivante ?\nCette action va créer une nouvelle référence dans l\'historique et incrémenter la version de toutes les checklists responsables.',
            confirmLabel: 'Valider et Archiver',
            onConfirm: () => createGlobalSnapshot()
        });
    };

    const handleExportDocx = async () => {
        if (!selectedResponsible || checklistQuestions.length === 0) return;
        try {
            const tableRows = [
                new TableRow({
                    children: [
                        new TableCell({ children: [new Paragraph({ text: "#", style: "TableHeader" })], width: { size: 5, type: WidthType.PERCENTAGE }, shading: { fill: "f3f4f6" } }),
                        new TableCell({ children: [new Paragraph({ text: "Question", style: "TableHeader" })], width: { size: 45, type: WidthType.PERCENTAGE }, shading: { fill: "f3f4f6" } }),
                        new TableCell({ children: [new Paragraph({ text: "Catégorie", style: "TableHeader" })], width: { size: 15, type: WidthType.PERCENTAGE }, shading: { fill: "f3f4f6" } }),
                        new TableCell({ children: [new Paragraph({ text: "Réponse", style: "TableHeader" })], width: { size: 10, type: WidthType.PERCENTAGE }, shading: { fill: "f3f4f6" } }),
                        new TableCell({ children: [new Paragraph({ text: "Justification", style: "TableHeader" })], width: { size: 25, type: WidthType.PERCENTAGE }, shading: { fill: "f3f4f6" } }),
                    ],
                }),
                ...checklistQuestions.map((q, index) => {
                    let responseText = "Non répondu";
                    let responseColor = "6b7280";
                    if (q.response === "yes") { responseText = "OUI"; responseColor = "16a34a"; }
                    if (q.response === "no") { responseText = "NON"; responseColor = "dc2626"; }
                    if (q.response === "na") { responseText = "N/A"; responseColor = "ca8a04"; }
                    return new TableRow({
                        children: [
                            new TableCell({ children: [new Paragraph({ text: (index + 1).toString() })] }),
                            new TableCell({ children: [new Paragraph({ text: q.question })] }),
                            new TableCell({ children: [new Paragraph({ text: q.category || "-" })] }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({ text: responseText, bold: true, color: responseColor })]
                                })]
                            }),
                            new TableCell({ children: [new Paragraph({ text: q.justification || "-" })] }),
                        ],
                    });
                })
            ];

            const doc = new Document({
                styles: {
                    paragraphStyles: [
                        { id: "TableHeader", name: "Table Header", run: { bold: true, size: 24, font: "Calibri" }, paragraph: { spacing: { before: 120, after: 120 } } },
                        { id: "Normal", name: "Normal", run: { size: 22, font: "Calibri" }, paragraph: { spacing: { before: 80, after: 80 } } },
                    ]
                },
                sections: [{
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: `Checklist de Suivi - ${selectedResponsible.name}`, bold: true, size: 32 })],
                            spacing: { after: 400 },
                        }),
                        new Paragraph({
                            children: [new TextRun({ text: `Date d'exportation: ${new Date().toLocaleDateString('fr-FR')}`, italics: true })],
                            spacing: { after: 400 },
                        }),
                        new Paragraph({
                            children: [new TextRun({ text: `Statut actuel: ${approvalStatus === 'approved' ? 'Approuvé' : approvalStatus === 'submitted' ? 'Soumis' : 'En attente'}`, bold: true })],
                            spacing: { after: 600 },
                        }),
                        new Table({
                            rows: tableRows,
                            width: { size: 100, type: WidthType.PERCENTAGE },
                            borders: {
                                top: { style: BorderStyle.SINGLE, size: 1, color: "d1d5db" },
                                bottom: { style: BorderStyle.SINGLE, size: 1, color: "d1d5db" },
                                left: { style: BorderStyle.SINGLE, size: 1, color: "d1d5db" },
                                right: { style: BorderStyle.SINGLE, size: 1, color: "d1d5db" },
                                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "e5e7eb" },
                                insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "e5e7eb" },
                            }
                        })
                    ],
                }],
            });

            const blob = await Packer.toBlob(doc);
            saveAs(blob, `Checklist_${selectedResponsible.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`);
        } catch (error) {
            console.error("Error generating DOCX:", error);
            setDialogConfig({ isOpen: true, type: 'alert', title: 'Erreur d\'exportation', message: 'Une erreur est survenue lors de la génération du document Word.', confirmLabel: 'Fermer' });
        }
    };

    const filteredMeasures = useMemo(() => {
        let measures = (sourceMeasures || []).filter((m) => m.retained !== false);
        const notAssignedValue = t('implementationChecklist.notAssigned');
        if (selectedResponsible) {
            const targetResp = (selectedResponsible.name || '').toLowerCase().trim();
            measures = measures.filter((m) => {
                let resp = m.responsiblePerson || m.responsibleParty;
                if (!resp || ['Non affecté', 'Not assigned', 'Unassigned'].includes(resp)) resp = notAssignedValue;
                return resp.toLowerCase().trim() === targetResp;
            });
        }
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            measures = measures.filter((m) =>
                (m.name || m.title || m.measureName || '').toLowerCase().includes(query) ||
                m.description?.toLowerCase().includes(query) ||
                (m.responsiblePerson || m.responsibleParty || '')?.toLowerCase().includes(query)
            );
        }
        Object.keys(selectedFilters).forEach((filterType) => {
            if (selectedFilters[filterType].length > 0) {
                measures = measures.filter((m) => selectedFilters[filterType].includes(m[filterType]));
            }
        });
        return measures;
    }, [sourceMeasures, searchQuery, selectedFilters, selectedResponsible, t]);

    const groupedFilteredMeasures = useMemo(() => {
        const grouped = {};
        filteredMeasures.forEach((measure) => {
            let key = measure[groupBy];
            if (!key) key = t('implementationChecklist.notDefined') || 'Non défini';
            if (Array.isArray(key)) key = key.length > 0 ? key[0] : t('implementationChecklist.notDefined') || 'Non défini';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(measure);
        });
        return grouped;
    }, [filteredMeasures, groupBy, t]);

    const toggleGroup = (groupKey) => {
        setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const clearFilters = () => {
        setSelectedFilters({ status: [], category: [], priority: [], timeline: [], source: [] });
        setSearchQuery('');
    };

    // Dashboard counts come from the same measures-table rows.
    const summary = useMemo(() => {
        const retained = (sourceMeasures || []).filter((m) => m.retained !== false);
        return {
            totalMeasures: retained.length,
            implemented: retained.filter((m) => (m.status || m.implementation) === 'implemented').length,
            inProgress: retained.filter((m) => (m.status || m.implementation) === 'in_progress').length,
        };
    }, [sourceMeasures]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">{t('implementationChecklist.loading')}</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-700">{error}</p>
                <button onClick={() => loadGlobalChecklist(analysisId)} className="mt-4 text-red-600 font-medium">{t('implementationChecklist.retry')}</button>
            </div>
        );
    }

    const metrics = selectedResponsible?.advancementMetrics || {};

    return (
        <div className="space-y-4">
            {isGenerating && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                        <Brain className="w-4 h-4 text-blue-600 animate-pulse" />
                        <span className="text-sm font-medium text-blue-900">
                            {t('implementationChecklist.generatingProgress', { percent: generationProgress }) || `Génération... ${generationProgress}%`}
                        </span>
                        <div className="flex-1 bg-blue-200 rounded-full h-2 overflow-hidden">
                            <div className="bg-blue-600 h-full transition-all duration-100" style={{ width: `${generationProgress}%` }} />
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                {selectedResponsible ? (
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <Users className="w-5 h-5 text-blue-600" />
                                {selectedResponsible.name}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                {filteredMeasures.length} mesures
                            </p>

                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg text-white text-sm font-semibold shadow-sm">
                                    <FileText size={14} />
                                    <span>Version {versionInfo.currentVersion}</span>
                                </div>

                                {approvalStatus === 'submitted' && (
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 border border-yellow-300 rounded-lg text-yellow-800 text-xs font-bold">
                                        <Clock size={12} />
                                        <span>En attente d'approbation</span>
                                    </div>
                                )}

                                {approvalStatus === 'approved' && (
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg text-green-800 text-xs font-bold">
                                        <CheckCircle size={12} />
                                        <span>Validée - Approuvée</span>
                                    </div>
                                )}

                                {approvalStatus === 'rejected' && (
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 border border-red-300 rounded-lg text-red-800 text-xs font-bold">
                                        <AlertCircle size={12} />
                                        <span>Rejetée (Correction requise)</span>
                                    </div>
                                )}

                                {versionInfo.isLocked && approvalStatus !== 'approved' && approvalStatus !== 'submitted' && (
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-gray-800 text-xs font-bold">
                                        <Lock size={12} />
                                        <span>Verrouillée</span>
                                    </div>
                                )}

                                {versionInfo.rejectionReason && approvalStatus === 'rejected' && (
                                    <div className="w-full mt-2 text-xs bg-red-50 border border-red-200 rounded p-2 text-red-800">
                                        <strong>Raison du rejet :</strong> {versionInfo.rejectionReason}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-100">
                            <button
                                onClick={handleExportDocx}
                                className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
                                title="Exporter en Word"
                            >
                                <Download className="w-4 h-4" />
                                <span className="hidden xl:inline">Export</span>
                            </button>

                            <div className="w-px h-6 bg-gray-200 mx-1"></div>

                            {!adminMode ? (
                                <>
                                    <button
                                        onClick={handleSaveChecklistResponses}
                                        disabled={isSaving || versionInfo.isLocked}
                                        className={`px-3 py-1.5 rounded text-xs font-bold flex items-center gap-2 transition-colors ${versionInfo.isLocked
                                            ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                            : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        <Save className="w-3.5 h-3.5" />
                                        {t('implementationChecklist.buttons.save')}
                                    </button>

                                    {(approvalStatus === 'draft' || approvalStatus === 'rejected' || !approvalStatus) && (
                                        <button
                                            onClick={handleSubmitChecklist}
                                            disabled={isSaving}
                                            className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700 flex items-center gap-2 shadow-sm"
                                        >
                                            <Send className="w-3.5 h-3.5" />
                                            {t('implementationChecklist.buttons.submit')}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    {approvalStatus === 'submitted' ? (
                                        <>
                                            <button
                                                onClick={handleApproveChecklist}
                                                disabled={isSaving}
                                                className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-bold hover:bg-green-700 flex items-center gap-2 shadow-sm"
                                            >
                                                <CheckCircle className="w-3.5 h-3.5" />
                                                Approuver
                                            </button>
                                            <button
                                                onClick={handleRejectChecklist}
                                                disabled={isSaving}
                                                className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-bold hover:bg-red-700 flex items-center gap-2 shadow-sm"
                                            >
                                                <AlertCircle className="w-3.5 h-3.5" />
                                                Rejeter
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-gray-500 italic">
                                                {approvalStatus === 'approved' ? 'Version approuvée' :
                                                    approvalStatus === 'rejected' ? 'Version rejetée' : 'En attente de soumission'}
                                            </span>
                                            {approvalStatus === 'approved' && (
                                                <button
                                                    onClick={handleUnlockChecklist}
                                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                                                    title="Déverrouiller"
                                                >
                                                    <Unlock className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col lg:flex-row gap-6 justify-between items-center">
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900">{t('implementationChecklist.globalContext.strategicDashboard')}</h2>
                            <div className="flex flex-wrap gap-4 mt-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-blue-50 rounded text-blue-600"><Layers className="w-3.5 h-3.5" /></div>
                                    <span className="text-sm font-medium">{summary.totalMeasures || 0} <span className="text-gray-500 text-xs">mesures</span></span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-green-50 rounded text-green-600"><CheckCircle className="w-3.5 h-3.5" /></div>
                                    <span className="text-sm font-medium">{summary.implemented || 0} <span className="text-gray-500 text-xs">fait</span></span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-yellow-50 rounded text-yellow-600"><Clock className="w-3.5 h-3.5" /></div>
                                    <span className="text-sm font-medium">{summary.inProgress || 0} <span className="text-gray-500 text-xs">en cours</span></span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-purple-50 rounded text-purple-600"><Shield className="w-3.5 h-3.5" /></div>
                                    <span className="text-sm font-medium">
                                        {(checklistData?.responsibleChecklists?.filter((r) => r.currentVersionData?.status === 'submitted') || []).length}
                                        <span className="text-gray-500 text-xs"> à approuver</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {isAdmin && (
                                <button
                                    onClick={handleCreateGlobalSnapshot}
                                    disabled={!(checklistData?.responsibleChecklists?.length > 0 && checklistData.responsibleChecklists.every((r) => r.currentVersionData?.status === 'approved'))}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold shadow-md transition-all ${(checklistData?.responsibleChecklists?.length > 0 && checklistData.responsibleChecklists.every((r) => r.currentVersionData?.status === 'approved'))
                                        ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-lg transform hover:-translate-y-0.5'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        }`}
                                    title={!(checklistData?.responsibleChecklists?.length > 0 && checklistData.responsibleChecklists.every((r) => r.currentVersionData?.status === 'approved'))
                                        ? "Toutes les checklists doivent être approuvées"
                                        : "Créer une nouvelle version globale"}
                                >
                                    <Archive className="w-4 h-4" />
                                    Valider Version
                                </button>
                            )}
                            <button
                                onClick={handleGenerateChecklist}
                                disabled={isGenerating}
                                className="px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 text-xs font-bold flex items-center gap-2 shadow-md"
                            >
                                <Brain className="w-3.5 h-3.5" />
                                {isGenerating ? t('implementationChecklist.generating') || 'Génération...' : (checklistGenerated ? 'Régénérer la Checklist' : 'Générer la Checklist')}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {checklistHistory.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                        onClick={() => setShowTimeline(!showTimeline)}
                        className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 hover:from-blue-100 hover:to-indigo-100 transition-all"
                    >
                        <div className="flex items-center gap-2">
                            {showTimeline ? <ChevronDown className="w-4 h-4 text-blue-600" /> : <ChevronRight className="w-4 h-4 text-blue-600" />}
                            <Clock className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-bold text-gray-900">
                                {t('implementationChecklist.timeline.title')} ({checklistHistory.length})
                            </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                            {isOnBranch && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-bold">{t('implementationChecklist.timeline.branchMode')}</span>}
                            <span className="font-medium">{t('implementationChecklist.timeline.currentVersion')}:</span>
                            <span className="font-bold">V{(currentVersionIndex !== null ? currentVersionIndex : checklistHistory.length - 1) + 1}</span>
                        </div>
                    </button>

                    {showTimeline && (
                        <div className="p-4 bg-gray-50 overflow-x-auto custom-scrollbar">
                            <div className="min-w-max relative" style={{ height: `${Math.max(200, (checklistHistory.reduce((max, _, i) => 3, 0)) * 100)}px` }}>
                                {(() => {
                                    const getParent = (v, i) => v.parentVersion !== null && v.parentVersion !== undefined ? v.parentVersion : (i > 0 ? i - 1 : null);
                                    const lanes = [-1];
                                    const nodeLanes = new Array(checklistHistory.length).fill(0);
                                    checklistHistory.forEach((v, i) => {
                                        const p = getParent(v, i);
                                        if (p === null) { nodeLanes[i] = 0; lanes[0] = i; }
                                        else {
                                            const pLane = nodeLanes[p];
                                            if (lanes[pLane] === p) { nodeLanes[i] = pLane; lanes[pLane] = i; }
                                            else { const newLane = lanes.length; lanes.push(i); nodeLanes[i] = newLane; }
                                        }
                                    });
                                    const GRID_X = 140; const GRID_Y = 100; const NODE_SIZE = 48;
                                    const totalHeight = Math.max(250, lanes.length * GRID_Y + 100);
                                    const totalWidth = Math.max(800, checklistHistory.length * GRID_X + 100);
                                    return (
                                        <div style={{ height: totalHeight, width: totalWidth, position: 'relative' }}>
                                            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
                                                {checklistHistory.map((v, i) => {
                                                    const p = getParent(v, i);
                                                    if (p === null) return null;
                                                    const startX = p * GRID_X + NODE_SIZE;
                                                    const startY = nodeLanes[p] * GRID_Y + NODE_SIZE / 2;
                                                    const endX = i * GRID_X;
                                                    const endY = nodeLanes[i] * GRID_Y + NODE_SIZE / 2;
                                                    let pathD;
                                                    if (nodeLanes[i] === nodeLanes[p]) pathD = `M ${startX} ${startY} L ${endX} ${endY}`;
                                                    else { const turnX = startX + 30; pathD = `M ${startX} ${startY} L ${turnX} ${startY} L ${turnX} ${endY} L ${endX} ${endY}`; }
                                                    return <path key={`edge-${i}`} d={pathD} stroke={nodeLanes[i] === nodeLanes[p] ? "#94a3b8" : "#8b5cf6"} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />;
                                                })}
                                            </svg>
                                            {checklistHistory.map((version, index) => {
                                                const isCurrentVersion = currentVersionIndex === index || (currentVersionIndex === null && index === checklistHistory.length - 1);
                                                const isBranchPoint = isOnBranch && currentVersionIndex === index;
                                                const parentIndex = version.parentVersion;
                                                const isBranch = parentIndex !== null && parentIndex !== undefined && parentIndex !== index - 1;
                                                const x = index * GRID_X; const y = nodeLanes[index] * GRID_Y;
                                                return (
                                                    <div key={index} className="absolute flex flex-col items-center group" style={{ left: x, top: y, width: NODE_SIZE, zIndex: 10 }}>
                                                        <button onClick={() => loadVersion(index)} className={`flex items-center justify-center w-12 h-12 rounded-full border-2 text-lg font-bold shadow-sm transition-transform hover:scale-110 z-10 bg-white ${isCurrentVersion ? isBranchPoint ? 'border-orange-500 text-orange-600 ring-4 ring-orange-100' : 'border-blue-500 text-blue-600 ring-4 ring-blue-100' : 'border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-500'}`}>
                                                            {index + 1}
                                                        </button>
                                                        <div className="absolute top-14 left-1/2 -translate-x-1/2 w-32 flex flex-col items-center text-center">
                                                            <span className={`text-xs font-semibold whitespace-nowrap ${isCurrentVersion ? 'text-gray-900' : 'text-gray-500'}`}>
                                                                {new Date(version.savedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                            </span>
                                                            <span className="text-[10px] text-gray-400">
                                                                {new Date(version.savedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                            <span className={`text-xs font-bold mt-0.5 ${version.statistics?.completionRate === 100 ? 'text-green-600' : 'text-blue-600'}`}>
                                                                {version.statistics?.completionRate || 0}%
                                                            </span>
                                                            {isBranch && (
                                                                <span className="text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full mt-1 border border-purple-200 shadow-sm font-semibold whitespace-nowrap">
                                                                    From V{parentIndex + 1}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="mb-4 bg-white rounded-lg border border-gray-200 p-1">
                <nav className="flex gap-2">
                    <button onClick={() => setActiveTab('checklist')} className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${activeTab === 'checklist' ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}>
                        <FileText className="w-4 h-4" />
                        {t('implementationChecklist.tabs.checklist')}
                        <span className="bg-white bg-opacity-50 px-1.5 rounded-full text-xs border border-blue-100 text-blue-600">{checklistQuestions.length}</span>
                    </button>
                    <button onClick={() => setActiveTab('measures')} className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${activeTab === 'measures' ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}>
                        <Shield className="w-4 h-4" />
                        {t('implementationChecklist.tabs.measures')}
                        <span className="bg-white bg-opacity-50 px-1.5 rounded-full text-xs border border-blue-100 text-blue-600">{summary.totalMeasures || 0}</span>
                    </button>
                </nav>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                {activeTab === 'checklist' && (
                    <div className="p-0">
                        {checklistQuestions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                {selectedResponsible ? (
                                    <>
                                        <Users className="w-12 h-12 text-gray-300 mb-2" />
                                        <h3 className="text-gray-900 font-medium">Aucune question assignée</h3>
                                        <p className="text-gray-500 text-sm mb-4">Ce responsable n'a aucune mesure associée nécessitant un suivi.</p>
                                    </>
                                ) : (
                                    <>
                                        <Brain className="w-12 h-12 text-gray-300 mb-2" />
                                        <h3 className="text-gray-900 font-medium">Aucune checklist générée</h3>
                                        <p className="text-gray-500 text-sm mb-4">Générez une checklist pour commencer le suivi.</p>
                                        <button onClick={handleGenerateChecklist} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-blue-700">Générer maintenant</button>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="border border-gray-200 rounded-lg overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-16">{t('implementationChecklist.table.headers.number')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('implementationChecklist.table.headers.question')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-36">{t('implementationChecklist.table.headers.responsible')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-32">{t('implementationChecklist.table.headers.category')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-48">{t('implementationChecklist.table.headers.status')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-20">{t('implementationChecklist.table.headers.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {checklistQuestions.filter((q) => !showOnlyUnanswered || q.response !== 'yes').map((q, i) => (
                                            <tr key={q.id || i} className="hover:bg-gray-50 transition-colors group">
                                                <td className="px-4 py-3 text-sm font-bold text-gray-900 align-top">{i + 1}</td>
                                                <td className="px-4 py-3 align-top">
                                                    <div className="text-sm text-gray-900 font-medium mb-1">{q.question}</div>
                                                    {q.concerned_measures && (
                                                        <div className="flex flex-wrap gap-1">
                                                            {q.concerned_measures.map((m, idx) => (
                                                                <span key={idx} className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100">{m}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 align-top">
                                                    <div className="flex items-center gap-1 flex-wrap">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                                                            <User className="w-3 h-3" />
                                                            {q.responsiblePerson || q._derivedResponsible || t('implementationChecklist.notAssigned')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 align-top"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{q.category || t('implementationChecklist.table.general')}</span></td>
                                                <td className="px-4 py-3 align-top">
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex bg-gray-100 rounded p-0.5 w-fit">
                                                            <button onClick={() => handleChecklistResponseChange(q.id, 'yes')} className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${q.response === 'yes' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}><CheckCircle className="w-3 h-3" />{t('implementationChecklist.table.response.yes')}</button>
                                                            <button onClick={() => handleChecklistResponseChange(q.id, 'no')} className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${q.response === 'no' ? 'bg-white text-red-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}><AlertTriangle className="w-3 h-3" />{t('implementationChecklist.table.response.no')}</button>
                                                        </div>
                                                        {q.response === 'no' && (
                                                            <textarea
                                                                placeholder={t('implementationChecklist.table.justificationPlaceholder')}
                                                                className="text-xs border border-gray-300 rounded p-1.5 w-full focus:ring-1 focus:ring-red-500 outline-none"
                                                                value={q.justification || ''}
                                                                onChange={(e) => handleJustificationChange(q.id, e.target.value)}
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 align-top">
                                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={() => openEditModal(q)} className="p-1.5 bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-100 transition-colors" title={t('common.edit')}>
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => handleDeleteQuestion(q.id)} className="p-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors" title={t('common.delete')}>
                                                            <Trash className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'measures' && (
                    <div className="p-4 space-y-3">
                        {Object.keys(groupedFilteredMeasures).map((groupKey) => (
                            <div key={groupKey} className="border border-gray-200 rounded-lg overflow-hidden">
                                <button onClick={() => toggleGroup(groupKey)} className="w-full px-4 py-2 bg-gray-50 flex items-center justify-between hover:bg-gray-100">
                                    <span className="font-bold text-xs uppercase text-gray-700">{groupKey} ({groupedFilteredMeasures[groupKey].length})</span>
                                    {expandedGroups[groupKey] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                </button>
                                {expandedGroups[groupKey] && (
                                    <div className="divide-y divide-gray-100 bg-white">
                                        {groupedFilteredMeasures[groupKey].map((m, i) => (
                                            <div key={i} className="p-3 flex gap-3 hover:bg-gray-50">
                                                <div className={`mt-0.5 ${m.status === 'implemented' ? 'text-green-500' : 'text-gray-300'}`}><CheckCircle className="w-4 h-4" /></div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{m.name || m.title || m.measureName}</div>
                                                    <div className="text-xs text-gray-500 mt-0.5">{m.description}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showAddQuestionModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
                        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <div className="p-2 bg-blue-50 rounded-lg"><Plus className="w-5 h-5 text-blue-600" /></div>
                                {t('implementationChecklist.modals.addQuestionTitle')}
                            </h3>
                            <button onClick={() => setShowAddQuestionModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <Zap className="w-5 h-5 rotate-45" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-thin scrollbar-thumb-gray-200">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">{t('implementationChecklist.modals.questionLabel')}</label>
                                    <textarea value={newQuestionText} onChange={(e) => setNewQuestionText(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-gray-50 hover:bg-white min-h-[120px]" placeholder={t('implementationChecklist.modals.questionPlaceholder')} autoFocus />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">{t('implementationChecklist.modals.categoryLabel')}</label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {[
                                            { key: 'technical', label: t('categories.technical') },
                                            { key: 'organizational', label: t('categories.organizational') },
                                            { key: 'human', label: t('categories.human') },
                                            { key: 'legal', label: t('categories.legal') },
                                            { key: 'manual', label: t('categories.manual') }
                                        ].map((cat) => (
                                            <button key={cat.key} onClick={() => setNewQuestionCategory(cat.label)} className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${newQuestionCategory === cat.label ? 'bg-blue-600 border-blue-600 text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'}`}>
                                                {cat.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('implementationChecklist.modals.concernedMeasures', { count: selectedMeasures.length })}
                                    </label>
                                    <div className="border border-gray-100 rounded-xl max-h-64 overflow-y-auto bg-gray-50 p-2 space-y-1">
                                        {sourceMeasures
                                            .filter((measure) => {
                                                if (selectedResponsible) {
                                                    const n = (s) => (s || '').toLowerCase().trim();
                                                    const notAssignedValue = t('implementationChecklist.notAssigned');
                                                    let measureResp = measure.responsiblePerson || measure.responsibleParty;
                                                    if (!measureResp || ['Non affecté', 'Not assigned', 'Unassigned'].includes(measureResp)) measureResp = notAssignedValue;
                                                    return n(measureResp) === n(selectedResponsible.name);
                                                }
                                                return true;
                                            })
                                            .map((measure) => (
                                                <label key={measure.id} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${selectedMeasures.some((m) => m.id === measure.id) ? 'bg-blue-50 border border-blue-100' : 'hover:bg-white border border-transparent hover:border-gray-200'}`}>
                                                    <input type="checkbox" checked={selectedMeasures.some((m) => m.id === measure.id)} onChange={() => toggleMeasureSelection(measure)} className="mt-1 w-4 h-4 text-blue-600 rounded ring-offset-2 focus:ring-2 focus:ring-blue-500" />
                                                    <div className="flex-1">
                                                        <div className="text-sm font-bold text-gray-900">{measure.name || measure.title || measure.measureName}</div>
                                                        <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-2">
                                                            <span className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-700">{measure.category}</span>
                                                            <span>•</span>
                                                            <span className="font-medium text-blue-600">
                                                                {(measure.responsiblePerson || measure.responsibleParty) && !['Non affecté', 'Not assigned', 'Unassigned'].includes(measure.responsiblePerson || measure.responsibleParty) ? (measure.responsiblePerson || measure.responsibleParty) : t('implementationChecklist.notAssigned')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </label>
                                            ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">{t('implementationChecklist.modals.responsibleLabel')}</label>
                                    <p className="text-xs text-gray-500 mb-2">{t('implementationChecklist.modals.responsibleHint')}</p>
                                    <div className="flex items-center gap-3">
                                        <input type="text" value={newQuestionResponsible} onChange={(e) => setNewQuestionResponsible(e.target.value)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-gray-50 hover:bg-white" placeholder={t('implementationChecklist.modals.responsiblePlaceholder')} />
                                        {newQuestionResponsible && (
                                            <button onClick={() => setNewQuestionResponsible('')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Effacer">
                                                <Zap className="w-4 h-4 rotate-45" />
                                            </button>
                                        )}
                                    </div>
                                    {selectedMeasures.length > 0 && !newQuestionResponsible && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {[...new Set(selectedMeasures.map((m) => {
                                                const resp = m.responsiblePerson || m.responsibleParty;
                                                if (!resp || ['Non affecté', 'Not assigned', 'Unassigned'].includes(resp)) return t('implementationChecklist.notAssigned');
                                                return resp;
                                            }))].map((resp, idx) => (
                                                <button key={idx} onClick={() => setNewQuestionResponsible(resp)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100 hover:bg-purple-100 transition-colors cursor-pointer">
                                                    <User className="w-3 h-3" />
                                                    {resp}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 flex justify-end gap-3 z-10">
                            <button onClick={() => { setShowAddQuestionModal(false); setNewQuestionText(''); setNewQuestionCategory('Technique'); setNewQuestionResponsible(''); setSelectedMeasures([]); }} className="px-6 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl font-bold transition-all">
                                {t('common.cancel')}
                            </button>
                            <button onClick={handleAddQuestion} disabled={!newQuestionText.trim()} className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:scale-100 font-bold flex items-center gap-2 transition-all">
                                <Plus className="w-5 h-5" />
                                {t('implementationChecklist.modals.createQuestion')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showEditQuestionModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
                        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <div className="p-2 bg-indigo-50 rounded-lg"><Edit className="w-5 h-5 text-indigo-600" /></div>
                                {t('implementationChecklist.modals.editQuestionTitle')}
                            </h3>
                            <button onClick={() => setShowEditQuestionModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <Zap className="w-5 h-5 rotate-45" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-thin scrollbar-thumb-gray-200">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">{t('implementationChecklist.modals.questionLabel')}</label>
                                    <textarea value={newQuestionText} onChange={(e) => setNewQuestionText(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all bg-gray-50 hover:bg-white min-h-[120px]" placeholder={t('implementationChecklist.modals.questionPlaceholder')} autoFocus />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">{t('implementationChecklist.modals.categoryLabel')}</label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {[
                                            { key: 'technical', label: t('categories.technical') },
                                            { key: 'organizational', label: t('categories.organizational') },
                                            { key: 'human', label: t('categories.human') },
                                            { key: 'legal', label: t('categories.legal') },
                                            { key: 'manual', label: t('categories.manual') }
                                        ].map((cat) => (
                                            <button key={cat.key} onClick={() => setNewQuestionCategory(cat.label)} className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${newQuestionCategory === cat.label ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-300'}`}>
                                                {cat.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('implementationChecklist.modals.concernedMeasures', { count: selectedMeasures.length })}
                                    </label>
                                    <div className="border border-gray-100 rounded-xl max-h-64 overflow-y-auto bg-gray-50 p-2 space-y-1">
                                        {sourceMeasures
                                            .filter((measure) => {
                                                if (selectedResponsible) {
                                                    const n = (s) => (s || '').toLowerCase().trim();
                                                    const notAssignedValue = t('implementationChecklist.notAssigned');
                                                    let measureResp = measure.responsiblePerson || measure.responsibleParty;
                                                    if (!measureResp || ['Non affecté', 'Not assigned', 'Unassigned'].includes(measureResp)) measureResp = notAssignedValue;
                                                    return n(measureResp) === n(selectedResponsible.name);
                                                }
                                                return true;
                                            })
                                            .map((measure) => (
                                                <label key={measure.id} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${selectedMeasures.some((m) => m.id === measure.id) ? 'bg-indigo-50 border border-indigo-100' : 'hover:bg-white border border-transparent hover:border-gray-200'}`}>
                                                    <input type="checkbox" checked={selectedMeasures.some((m) => m.id === measure.id)} onChange={() => toggleMeasureSelection(measure)} className="mt-1 w-4 h-4 text-indigo-600 rounded ring-offset-2 focus:ring-2 focus:ring-indigo-500" />
                                                    <div className="flex-1">
                                                        <div className="text-sm font-bold text-gray-900">{measure.name || measure.title || measure.measureName}</div>
                                                        <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-2">
                                                            <span className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-700">{measure.category}</span>
                                                            <span>•</span>
                                                            <span className="font-medium text-indigo-600">
                                                                {(measure.responsiblePerson || measure.responsibleParty) && !['Non affecté', 'Not assigned', 'Unassigned'].includes(measure.responsiblePerson || measure.responsibleParty) ? (measure.responsiblePerson || measure.responsibleParty) : t('implementationChecklist.notAssigned')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </label>
                                            ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">{t('implementationChecklist.modals.responsibleLabel')}</label>
                                    <p className="text-xs text-gray-500 mb-2">{t('implementationChecklist.modals.responsibleHint')}</p>
                                    <div className="flex items-center gap-3">
                                        <input type="text" value={newQuestionResponsible} onChange={(e) => setNewQuestionResponsible(e.target.value)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all bg-gray-50 hover:bg-white" placeholder={t('implementationChecklist.modals.responsiblePlaceholder')} />
                                        {newQuestionResponsible && (
                                            <button onClick={() => setNewQuestionResponsible('')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Effacer">
                                                <Zap className="w-4 h-4 rotate-45" />
                                            </button>
                                        )}
                                    </div>
                                    {selectedMeasures.length > 0 && !newQuestionResponsible && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {[...new Set(selectedMeasures.map((m) => {
                                                const resp = m.responsiblePerson || m.responsibleParty;
                                                if (!resp || ['Non affecté', 'Not assigned', 'Unassigned'].includes(resp)) return t('implementationChecklist.notAssigned');
                                                return resp;
                                            }))].map((resp, idx) => (
                                                <button key={idx} onClick={() => setNewQuestionResponsible(resp)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100 hover:bg-purple-100 transition-colors cursor-pointer">
                                                    <User className="w-3 h-3" />
                                                    {resp}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 flex justify-end gap-3 z-10">
                            <button onClick={() => { setShowEditQuestionModal(false); setEditingQuestion(null); setNewQuestionText(''); setNewQuestionCategory('Technique'); setNewQuestionResponsible(''); setSelectedMeasures([]); }} className="px-6 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl font-bold transition-all">
                                {t('common.cancel')}
                            </button>
                            <button onClick={handleEditQuestion} disabled={!newQuestionText.trim()} className="px-8 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:scale-100 font-bold flex items-center gap-2 transition-all">
                                <Save className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {dialogConfig.isOpen && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-300">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 transform transition-all scale-100 opacity-100">
                        <div className="px-6 py-8 flex flex-col items-center text-center">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${dialogConfig.type === 'alert' ? 'bg-amber-100 text-amber-600' : dialogConfig.type === 'prompt' ? 'bg-blue-100 text-blue-600' : 'bg-red-50 text-red-600'}`}>
                                {dialogConfig.type === 'alert' ? <AlertTriangle className="w-8 h-8" /> : dialogConfig.type === 'prompt' ? <Send className="w-8 h-8" /> : <Info className="w-8 h-8" />}
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{dialogConfig.title}</h3>
                            <p className="text-gray-600 mb-6 whitespace-pre-wrap">{dialogConfig.message}</p>
                            {dialogConfig.type === 'prompt' && (
                                <div className="w-full mb-6 text-left">
                                    <textarea autoFocus value={dialogConfig.inputValue} onChange={(e) => setDialogConfig((prev) => ({ ...prev, inputValue: e.target.value }))} placeholder={dialogConfig.placeholder} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all min-h-[100px] resize-none" />
                                </div>
                            )}
                            <div className="flex gap-3 w-full">
                                {dialogConfig.type !== 'alert' && (
                                    <button onClick={() => { if (dialogConfig.onCancel) dialogConfig.onCancel(); setDialogConfig((prev) => ({ ...prev, isOpen: false })); }} className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors">
                                        {dialogConfig.cancelLabel || 'Annuler'}
                                    </button>
                                )}
                                <button onClick={() => { if (dialogConfig.onConfirm) { if (dialogConfig.type === 'prompt') dialogConfig.onConfirm(dialogConfig.inputValue); else dialogConfig.onConfirm(); } setDialogConfig((prev) => ({ ...prev, isOpen: false, inputValue: '' })); }} className={`flex-1 px-6 py-3 text-white font-bold rounded-xl shadow-lg transition-transform active:scale-95 ${dialogConfig.type === 'alert' ? 'bg-gray-900' : dialogConfig.type === 'prompt' ? 'bg-blue-600 shadow-blue-200' : 'bg-red-600 shadow-red-200'}`}>
                                    {dialogConfig.confirmLabel || 'OK'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default GlobalUnifiedChecklist;
